import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { STATUS_LABELS, formatDateIT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StatusBadge from "@/components/app/StatusBadge";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Mail, Package, Printer, QrCode, User } from "lucide-react";
import { openBinary } from "@/lib/pdfHelpers";

export default function CaseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/repairs/cases/${id}`).then((r) => setData(r.data)); }, [id]);

  const sendAcceptanceEmail = async () => {
    try {
      const r = await api.post(`/repairs/cases/${id}/email-acceptance`);
      if (r.data.status === "not_configured") toast.warning("Servizio email non configurato. Comunicazione registrata come bozza.");
      else if (r.data.status === "sent") toast.success("Verbale inviato via email.");
      else toast.error("Errore invio: " + r.data.error);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };

  if (!data) return <div className="p-6">Caricamento…</div>;
  const { case: c, items } = data;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="case-detail-page">
      <div>
        <Link to="/pratiche" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Pratiche</Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-1">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Pratica di assistenza</div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">{c.case_number}</h1>
            <div className="text-sm text-slate-600 mt-1">{c.customer_snapshot?.company_name} · {formatDateIT(c.created_at)}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="case-download-acceptance" variant="outline" onClick={() => openBinary(`/repairs/cases/${c.id}/acceptance.pdf`)}><FileDown className="w-4 h-4 mr-1" /> Verbale accettazione</Button>
            {c.status === "chiusa" && <Button variant="outline" onClick={() => openBinary(`/repairs/cases/${c.id}/delivery.pdf`)}><FileDown className="w-4 h-4 mr-1" /> Verbale riconsegna</Button>}
            <Button data-testid="case-email-acceptance" onClick={sendAcceptanceEmail} className="ama-btn-primary rounded-full"><Mail className="w-4 h-4 mr-1" /> Invia via email</Button>
          </div>
        </div>
      </div>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Cliente e consegna</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Ragione sociale" value={c.customer_snapshot?.company_name} />
            <Field label="Codice cliente" value={c.customer_snapshot?.customer_code} />
            <Field label="P.IVA" value={c.customer_snapshot?.vat_number} />
            <Field label="Città" value={c.customer_snapshot?.city} />
            <Field label="Persona che consegna" value={c.delivering_person} />
            <Field label="Telefono" value={c.delivering_phone} />
            <Field label="Email" value={c.delivering_email} />
            <Field label="Operatore" value={c.operator_name} />
          </div>
        </CardContent>
      </Card>

      <Card className="ama-card border-0 shadow-none" data-testid="case-qr-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#d63384]" /> QR pratica
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/repairs/cases/${c.id}/qr.png?t=${encodeURIComponent(c.updated_at || c.created_at || "")}`}
                alt={`QR pratica ${c.case_number}`}
                width={168}
                height={168}
                data-testid="case-qr-image"
              />
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-sm text-slate-600">
                Inquadrando questo QR, un operatore AMA accede alla scheda interna della pratica;
                un cliente atterra sulla pagina pubblica con lo stato aggiornato e (se disponibile) il link al preventivo.
              </div>
              <div className="text-xs text-slate-500">Il codice è opaco, revocabile e non contiene dati sensibili.</div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  data-testid="case-print-qr-label"
                  variant="outline"
                  onClick={() => openBinary(`/repairs/cases/${c.id}/qr-label.pdf?t=${Date.now()}`)}
                >
                  <Printer className="w-4 h-4 mr-1" /> Stampa etichetta A6
                </Button>
                <Button
                  data-testid="case-download-qr-png"
                  variant="outline"
                  onClick={() => openBinary(`/repairs/cases/${c.id}/qr.png?t=${Date.now()}`)}
                >
                  <FileDown className="w-4 h-4 mr-1" /> Scarica QR PNG
                </Button>
                <Button
                  data-testid="case-rotate-qr"
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm("Rigenerare il QR? Il codice precedente non funzionerà più.")) return;
                    try {
                      await api.post(`/repairs/cases/${c.id}/qr/rotate`);
                      const r = await api.get(`/repairs/cases/${id}`);
                      setData(r.data);
                      toast.success("Nuovo QR generato. Ristampa l'etichetta.");
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore rotazione QR"); }
                  }}
                >
                  Rigenera
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-heading font-bold text-[#0a0a2e]">Prodotti ({items.length})</h2>
        {items.map((it) => (
          <Link to={`/prodotti/${it.id}`} key={it.id} className="ama-card p-4 flex flex-col md:flex-row md:items-center gap-4 hover:border-[#d63384] transition" data-testid={`case-item-${it.item_number}`}>
            <div className="w-10 h-10 rounded-lg bg-fuchsia-50 flex items-center justify-center text-[#d63384]"><Package className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[#0a0a2e]">{it.item_number} — {it.manufacturer_name} {it.model}</div>
                <StatusBadge status={it.current_status} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5">SN {it.serial_number || "-"} · {it.product_code} · {it.warranty_status}</div>
              <div className="text-sm text-slate-700 mt-1 line-clamp-2"><b className="text-[#0a0a2e]">Problema:</b> {it.declared_problem}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-[#0a0a2e] font-medium">{value || "-"}</div>
    </div>
  );
}
